import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardActionArea,
  Grid
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import type { RouteProfile } from '../profileSelector/RouteProfileSelector';

// Import the same icons used in MyRoutesPage
import TrekkingIcon from '../../assets/bike_profile_icons/BikeProfile_Trekking_light.svg';
import RoadBikeIcon from '../../assets/bike_profile_icons/BikeProfile_RoadBike_light.svg';
import GravelBikeIcon from '../../assets/bike_profile_icons/BikeProfile_GravelBike_light.svg';
import MountainBikeIcon from '../../assets/bike_profile_icons/BikeProfile_MountainBike_light.svg';

interface BikeProfileSelectorProps {
  selectedProfile: RouteProfile;
  onProfileChange: (profile: RouteProfile) => void;
  onNext?: (profile: RouteProfile) => void;
}

const bikeProfiles = [
  {
    id: 'road' as RouteProfile,
    name: 'Road Bike',
    description: 'Optimized for paved roads and smooth surfaces',
    icon: RoadBikeIcon
  },
  {
    id: 'trekking' as RouteProfile,
    name: 'Trekking',
    description: 'Balanced for mixed terrain and touring',
    icon: TrekkingIcon
  },
  {
    id: 'gravel' as RouteProfile,
    name: 'Gravel',
    description: 'Perfect for gravel roads and light off-road',
    icon: GravelBikeIcon
  },
  {
    id: 'mtb' as RouteProfile,
    name: 'Mountain Bike',
    description: 'Built for trails and challenging terrain',
    icon: MountainBikeIcon
  }
];

export const BikeProfileSelector: React.FC<BikeProfileSelectorProps> = ({
  selectedProfile,
  onProfileChange,
  onNext
}) => {
  const handleProfileSelect = (profile: RouteProfile) => {
    onProfileChange(profile);
  };

  const handleNext = () => {
    if (onNext) {
      onNext(selectedProfile);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Bike Profile
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose the bike profile to optimize your GPX route. This affects route preferences and surface types.
      </Typography>

      {/* Compact horizontal layout for mobile */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={1}>
          {bikeProfiles.map((profile) => (
            <Grid item xs={6} sm={3} key={profile.id}>
              <Card
                sx={{
                  border: selectedProfile === profile.id ? 2 : 1,
                  borderColor: selectedProfile === profile.id ? 'primary.main' : 'divider',
                  position: 'relative',
                  minHeight: { xs: '85px', sm: '95px' },
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardActionArea 
                  onClick={() => handleProfileSelect(profile.id)}
                  sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0.5,
                    height: '100%'
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    gap: 0.25,
                    textAlign: 'center'
                  }}>
                    <img
                      src={profile.icon}
                      alt={profile.name}
                      style={{ width: 48, height: 48 }}
                    />
                    <Typography variant="caption" component="div" sx={{ 
                      fontSize: '0.65rem',
                      fontWeight: selectedProfile === profile.id ? 600 : 400,
                      lineHeight: 1,
                      color: selectedProfile === profile.id ? 'primary.main' : 'text.primary'
                    }}>
                      {profile.name}
                    </Typography>
                    {selectedProfile === profile.id && (
                      <CheckIcon color="primary" sx={{ fontSize: '1rem', position: 'absolute', top: 4, right: 4 }} />
                    )}
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Only show Next button if onNext callback is provided */}
      {onNext && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!selectedProfile}
          >
            Next: Preview Route
          </Button>
        </Box>
      )}
    </Box>
  );
};
